import {
  columnSizingFeature,
  columnVisibilityFeature,
  flexRender,
  tableFeatures,
  useTable,
} from '@tanstack/react-table';
import { get, isString } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { FixedSizeList } from 'react-window';
import styled, { css } from 'styled-components';

import { hexToRgba } from '../../helpers/utils/common';

import { Text } from '../typography';
import { COLORS } from '../../helpers/constants/styleguide';
import {
  V7_ROW_PROPS,
  adaptCellRenderer,
  toColumnDefs,
  toColumnVisibility,
  toHiddenHeaderIds,
} from './columnAdapter';

const GUTTER_SIZE = 10;
const ITEM_SIZE = 74;

// v8 has no layout plugin, so reproduce the sizing useFlexLayout used to emit
const sizeStyle = size => ({
  boxSizing: 'border-box',
  flex: `${size} 0 auto`,
  minWidth: '0px',
  width: `${size}px`,
});

const DefaultRowCellComponent = ({ cell }) => {
  return (
    <Text weight="medium" color="dark" truncate paragraph smallTracking>
      {cell.value}
    </Text>
  );
};

DefaultRowCellComponent.propTypes = {
  cell: PropTypes.shape({ value: PropTypes.node }).isRequired,
};

const features = tableFeatures({
  columnSizingFeature,
  columnVisibilityFeature,
});

const VirtualizeCardTable = ({
  columns,
  data,
  getCustomRowProps,
  tableIndex = 0,
  tableName,
  colDefaultWidth = 100,
  align = 'center',
  fitToContent = false,
  virtualizedOptions,
}) => {
  const columnDefs = useMemo(() => toColumnDefs(columns), [columns]);
  const columnVisibility = useMemo(
    () => toColumnVisibility(columns),
    [columns],
  );
  const hiddenHeaderIds = useMemo(() => toHiddenHeaderIds(columns), [columns]);
  const defaultColumn = useMemo(
    () => ({
      size: colDefaultWidth,
      cell: adaptCellRenderer(DefaultRowCellComponent),
    }),
    [colDefaultWidth],
  );

  const table = useTable({
    features,
    data,
    columns: columnDefs,
    defaultColumn,
    state: { columnVisibility },
  });

  const rows = table.getRowModel().rows;

  const RenderRow = useCallback(
    ({ index, style }) => {
      const row = rows[index];
      const rowId = `${tableName || 'table'}${tableIndex}-row${index}`;
      const rowProps = getCustomRowProps
        ? { ...V7_ROW_PROPS, ...getCustomRowProps(V7_ROW_PROPS, { row }) }
        : V7_ROW_PROPS;

      return (
        <Row
          {...rowProps}
          style={{
            ...style,
            top: style.top + GUTTER_SIZE,
            height: style.height - GUTTER_SIZE,
            left: style.left + GUTTER_SIZE,
            width: `calc(${style.width} - ${GUTTER_SIZE * 2}px)`,
          }}
          tabIndex="0"
          className="table-row"
          id={rowId}
        >
          {row.getVisibleCells().map(cell => (
            <RowCell
              key={cell.id}
              role="cell"
              align={align}
              style={sizeStyle(cell.column.getSize())}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </RowCell>
          ))}
        </Row>
      );
    },
    [rows, align, getCustomRowProps, tableIndex, tableName],
  );

  return (
    <Container>
      <Table role="table" data-private fitToContent={fitToContent}>
        {table.getHeaderGroups().map(headerGroup => (
          <HeaderContainer role="row" key={headerGroup.id}>
            {headerGroup.headers.map(header => {
              const { header: headerDef } = header.column.columnDef;
              return (
                <HeaderCell
                  key={header.id}
                  role="columnheader"
                  colSpan={header.colSpan}
                  align={align}
                  style={{
                    ...sizeStyle(header.getSize()),
                    ...(hiddenHeaderIds.has(header.column.id)
                      ? { display: 'none' }
                      : {}),
                  }}
                >
                  {isString(headerDef) ? (
                    <Text weight="medium" size="extraSmall">
                      {flexRender(headerDef, header.getContext())}
                    </Text>
                  ) : (
                    flexRender(headerDef, header.getContext())
                  )}
                </HeaderCell>
              );
            })}
          </HeaderContainer>
        ))}

        <BodyContainer>
          <FixedSizeList
            height={get(virtualizedOptions, 'height', 0)}
            ref={get(virtualizedOptions, 'ref')}
            onItemsRendered={get(virtualizedOptions, 'onItemsRendered')}
            itemCount={rows.length}
            itemSize={ITEM_SIZE + GUTTER_SIZE}
          >
            {RenderRow}
          </FixedSizeList>
        </BodyContainer>
      </Table>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  overflow: scroll;
`;

const Table = styled.div`
  width: ${({ fitToContent }) => (fitToContent ? 'fit-content' : '100%')};
  min-width: unset !important; //useFlexLayout set min-width to totalColumnsWidth this not good for us in small screens
`;

const HeaderContainer = styled.div`
  display: flex;
  width: ${`calc(100% - ${GUTTER_SIZE * 2}px)`};
  height: 48px;
  padding: 16px 16px;
  margin-left: ${GUTTER_SIZE}px;
  align-items: center;
  border-radius: 3px;
  background-blend-mode: multiply;
  background-color: ${hexToRgba(COLORS.azure, 0.09)};
`;

const BodyContainer = styled.div`
  & > div {
    margin-bottom: 8px;
  }
`;

const rowValidStyle = css`
  border-left: solid 6px ${COLORS.orange} !important;
  &.error-focus {
    box-shadow: 0 2px 10px 2px rgba(214, 221, 231, 0.81);
    border: solid 1px #f2f4f7;
    background-color: #ffffff;
  }
`;

const Row = styled.div`
  display: inline-flex;
  width: 100%;
  justify-content: center;
  padding: 16px 16px;
  border-radius: 6px;
  border: solid 1px #ebeff4;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;

  :first-child {
    text-align: left;
  }

  :hover {
    box-shadow: 0 2px 10px 2px rgba(214, 221, 231, 0.81);
    border: solid 1px #f2f4f7;
    background-color: #ffffff;
  }

  :focus {
    box-shadow: 0 0 5px 0 rgba(0, 166, 255, 0.5);
    border: solid 1px #41beff;
    outline: none;
  }

  :active {
    box-shadow: 0 2px 10px 2px rgba(214, 221, 231, 0.81);
    border: solid 1px #f2f4f7;
    background-color: #f9fafc;
  }

  ${({ valid }) => valid === false && rowValidStyle}
`;

/*
 * The use of justify-content in this component it to support our current web-app HeaderCell component
 * when using HeaderCell component align will not work and without it tooltip will break
 *
 * Todo: update tooltip design and HeaderCell to support normal align
 * */
const HeaderCell = styled.div`
  flex-shrink: 1 !important;
  flex-flow: nowrap;
  overflow: hidden;
  padding: 8px 8px;
  text-align: ${({ align }) => align};

  > * {
    justify-content: center;
  }

  :first-child {
    text-align: left;

    > * {
      justify-content: flex-start;
    }
  }

  :last-child {
    text-align: right;

    > * {
      justify-content: flex-end;
    }
  }
`;

const RowCell = styled.div`
  flex-flow: row nowrap;
  flex-shrink: 1 !important;
  flex-basis: auto;
  overflow: hidden;
  padding: 8px 8px;
  text-align: ${({ align }) => align};

  :first-child {
    text-align: left;
  }
  :last-child {
    text-align: right;
  }
`;

VirtualizeCardTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      accessor: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
        .isRequired,
      id: PropTypes.string,
      columns: PropTypes.array,
      Header: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func,
        PropTypes.element,
      ]),
      Cell: PropTypes.oneOfType([PropTypes.func, PropTypes.elementType]),
      width: PropTypes.number,
      minWidth: PropTypes.number,
      maxWidth: PropTypes.number,
    }),
  ).isRequired,
  getCustomRowProps: PropTypes.func,
  tableName: PropTypes.string,
  virtualizedOptions: PropTypes.object,
  tableIndex: PropTypes.number,
  colDefaultWidth: PropTypes.number,
  fitToContent: PropTypes.bool,
  align: PropTypes.PropTypes.oneOf(['center', 'left', 'right']),
};

export default VirtualizeCardTable;
